import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Building, Mail, MessageCircle, ExternalLink } from "lucide-react";
import { usePageTitle } from "../hooks/usePageTitle";
import { trackWhatsApp } from "../lib/beacon";

/**
 * Página institucional /compliance
 * Contém dados da empresa. Os campos marcados como "A preencher" devem ser
 * atualizados pelo administrador com os dados reais (CNPJ, endereço fiscal
 * etc.). Manter valores neutros até a atualização evita reprovar em Google
 * Ads / Meta Ads por informação falsa.
 */
const Row = ({ label, value }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-3 border-b border-slate-100 last:border-0">
    <dt className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
    <dd className="sm:col-span-2 text-slate-800">{value}</dd>
  </div>
);

const SUPPORT_WA = (process.env.REACT_APP_SUPPORT_WA || "+5511996420785").replace(/[^0-9]/g, "");

export default function Compliance() {
  usePageTitle(
    "Compliance e dados institucionais",
    "Dados institucionais da RAF Direct: razão social, CNPJ, endereço, contatos oficiais e informações de responsabilidade técnica e proteção de dados."
  );
  return (
    <div className="bg-white" data-testid="compliance-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-700" />
          Compliance · Dados institucionais
        </div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0B2B62]">Sobre a RAF Direct</h1>
        <p className="mt-3 text-slate-600 leading-relaxed max-w-2xl">
          Esta página reúne as informações institucionais, contatos oficiais e a natureza do serviço
          prestado pela RAF Direct. Utilizamos esta página para atender exigências de transparência
          das plataformas de anúncios e das autoridades regulatórias.
        </p>

        {/* Natureza do serviço */}
        <section className="mt-10 rounded-2xl border border-slate-200 p-6 bg-slate-50">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
            <Building className="h-3.5 w-3.5" />
            Natureza do serviço
          </div>
          <p className="mt-3 text-slate-700 leading-relaxed">
            A RAF Direct é uma <strong>plataforma digital de orientação, informação e encaminhamento</strong>
            {" "}que conecta famílias a clínicas e profissionais parceiros. <strong>Não prestamos
            diagnóstico, prescrição ou tratamento</strong>. Os serviços clínicos são prestados por parceiros
            credenciados, sob a responsabilidade técnica exclusiva destes, com seus respectivos registros
            junto aos conselhos profissionais (CRM, CRP, COREN, etc.).
          </p>
        </section>

        {/* Dados institucionais */}
        <section className="mt-8">
          <h2 className="text-xl font-bold text-[#0B2B62]">Dados institucionais</h2>
          <dl className="mt-4 rounded-2xl border border-slate-200 bg-white px-5">
            <Row label="Nome fantasia" value="RAF Direct" />
            <Row label="Razão social" value="RAF - REDE DE APOIO AOS FAMILIARES LTDA" />
            <Row label="CNPJ" value="61.281.627/0001-52 (Matriz)" />
            <Row label="Inscrita desde" value="12/06/2025" />
            <Row
              label="Endereço fiscal"
              value={
                <>
                  Rua Basílio Puntel, 500 · Sala 1
                  <br />
                  Jardim Cardoso (Mailasqui) · São Roque/SP
                  <br />
                  CEP 18.143-810
                </>
              }
            />
            <Row label="Site oficial" value={<a href="https://rafdirect.com.br" className="text-[#0B2B62] underline">rafdirect.com.br</a>} />
          </dl>
          <p className="mt-3 text-xs text-slate-500">
            Informações extraídas do Cadastro Nacional da Pessoa Jurídica (CNPJ), Receita Federal do Brasil.
          </p>
        </section>

        {/* Contatos oficiais */}
        <section className="mt-10">
          <h2 className="text-xl font-bold text-[#0B2B62]">Contatos oficiais</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <a
              href="mailto:contato@rafdirect.com.br"
              className="rounded-2xl border border-slate-200 p-5 bg-white hover:border-[#0B2B62] hover:shadow-md transition flex items-start gap-3"
              data-testid="compliance-email"
            >
              <Mail className="h-5 w-5 text-[#0B2B62] mt-0.5" />
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                  E-mail (atendimento e DPO/LGPD)
                </div>
                <div className="mt-1 font-semibold text-slate-900">contato@rafdirect.com.br</div>
              </div>
            </a>
            <a
              href={`https://wa.me/${SUPPORT_WA}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsApp("compliance_page")}
              className="rounded-2xl border border-slate-200 p-5 bg-white hover:border-[#25D366] hover:shadow-md transition flex items-start gap-3"
              data-testid="compliance-whatsapp"
            >
              <MessageCircle className="h-5 w-5 text-[#25D366] mt-0.5" />
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">WhatsApp</div>
                <div className="mt-1 font-semibold text-slate-900">
                  +{SUPPORT_WA.replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, "$1 $2 $3-$4")}
                </div>
                <div className="mt-0.5 text-xs text-slate-500">Atendimento 24h · Seg. a Dom.</div>
              </div>
            </a>
          </div>
        </section>

        {/* Responsabilidade técnica */}
        <section className="mt-10">
          <h2 className="text-xl font-bold text-[#0B2B62]">Responsabilidade técnica</h2>
          <p className="mt-3 text-slate-700 leading-relaxed">
            Cada clínica e profissional credenciado é <strong>responsável técnico exclusivo</strong> pelos
            serviços que presta, mantendo em dia seus registros profissionais e alvarás. A RAF Direct
            faz curadoria e validação documental básica no momento do credenciamento e monitora
            recorrentemente a regularidade dos parceiros.
          </p>
        </section>

        {/* Segurança & LGPD */}
        <section className="mt-10">
          <h2 className="text-xl font-bold text-[#0B2B62]">Segurança e proteção de dados</h2>
          <ul className="mt-4 space-y-2 text-slate-700 text-[15px] list-disc pl-6">
            <li>Criptografia em trânsito (HTTPS/TLS) em todas as comunicações.</li>
            <li>Controle de acesso por perfil e autenticação em dois fatores para operadores.</li>
            <li>Auditoria hash-chained em ações sensíveis (LGPD Art. 46).</li>
            <li>Pagamentos processados por gateway certificado (Stripe) — não armazenamos dados de cartão.</li>
            <li>Conformidade LGPD (Lei 13.709/2018). Contato do DPO: contato@rafdirect.com.br.</li>
          </ul>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/politica-de-privacidade"
              className="inline-flex items-center gap-1.5 text-sm rounded-lg border border-slate-200 px-4 py-2 text-[#0B2B62] hover:border-[#0B2B62] transition"
            >
              Política de Privacidade
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
            <Link
              to="/termos-de-uso"
              className="inline-flex items-center gap-1.5 text-sm rounded-lg border border-slate-200 px-4 py-2 text-[#0B2B62] hover:border-[#0B2B62] transition"
            >
              Termos de Uso
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
            <Link
              to="/atendimento"
              className="inline-flex items-center gap-1.5 text-sm rounded-lg border border-slate-200 px-4 py-2 text-[#0B2B62] hover:border-[#0B2B62] transition"
            >
              Página institucional /atendimento
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </div>
        </section>

        <div className="mt-12 border-t pt-6 text-xs text-slate-500">
          Última revisão desta página: 05 de fevereiro de 2026.
        </div>
      </div>
    </div>
  );
}
